import { type PaletteKey, type PixelCanvas, sprite } from '../../../lib';
import { box, byRow, geometry, outlined, round, taper } from '../effect-guns.sprite';

/**
 * Blunderbuss (the Pirate set's gun, ADR-0160 amended): a walnut stock with a brass butt plate, a brass barrel ringed
 * with bands flaring into a wide bell mouth, a matchlock's serpentine over the lock holding a smouldering match. On a shot
 * (`fire`) the bell's mouth flashes white, then gold and orange, then settles.
 */

const WOOD = ['N', 'n', 'm', 't'] as const;
const BRASS = ['u', 'U', 'Y', 'y'] as const;

const blunderbuss = (frame: number, heat = 0) =>
  outlined((c: PixelCanvas) => {
    // The stock: walnut, a heavy butt dropping behind the grip, its wrist, the fore-stock under the barrel.
    box(c, 2, 16, 12, 28, [2, 1], byRow(16, 28, WOOD, 2, 2), 'l');
    box(c, 10, 17, 24, 25, [0], byRow(17, 25, WOOD, 1, 2), '');
    box(c, 22, 20, 46, 24, [0, 1], byRow(20, 24, WOOD, 1, 1), 'r');
    // The grain.
    for (const [x, y] of [
      [5, 20],
      [6, 20],
      [7, 21],
      [8, 24],
      [9, 24],
      [15, 21],
      [16, 21],
      [30, 22],
      [31, 22],
      [38, 22],
    ] as const)
      c.set(x, y, 'n');
    // The brass butt plate.
    for (let y = 17; y <= 27; y++) c.set(2, y, y < 20 ? 'y' : 'Y');
    // The barrel: brass, round, over the fore-stock, its bands.
    taper(c, 20, 50, 17, 2.6, 2.2, round(BRASS));
    for (const x of [28, 38, 47]) for (let y = 14; y <= 20; y++) c.set(x, y, y === 14 ? 'y' : y > 18 ? 'u' : 'U');
    // The bell: flaring wide to the muzzle, its mouth a dark hole, rimmed.
    for (let x = 50; x <= 62; x++) {
      const h = 2.2 + 6 * ((x - 50) / 12) ** 2;
      const [a, b] = [Math.round(17 + 0.25 * (x - 50) - h), Math.round(17 + 0.25 * (x - 50) + h)];
      for (let y = a; y <= b; y++) c.set(x, y, y === a ? 'y' : y >= b - 1 ? 'u' : y < a + 3 ? 'Y' : 'U');
    }
    for (let y = 13; y <= 27; y++) {
      const cy = 20;
      const e = Math.abs(y - cy) / 6.5;
      if (e > 1) continue;
      const k: PaletteKey = heat >= 2 ? 'w' : heat === 1 ? (e < 0.5 ? 'y' : 'o') : e > 0.7 ? '1' : 'k';
      c.set(61, y, k);
      c.set(62, y, e > 0.85 ? 'Y' : y < cy ? 'y' : 'Y');
    }
    // The lock plate and the serpentine arm up over it, its jaws holding the match.
    box(c, 24, 18, 30, 21, [0], (x, y): PaletteKey => (y === 18 ? 'y' : x === 30 ? 'u' : 'U'));
    for (const [x, y, k] of [
      [26, 17, '2'],
      [26, 16, '3'],
      [25, 15, '3'],
      [25, 14, '2'],
      [26, 13, '3'],
      [27, 12, '4'],
      [28, 12, '3'],
    ] as const)
      c.set(x, y, k);
    // The match: a cord, its end smouldering (flickering over the idle frames).
    c.set(28, 11, 'e');
    c.set(27, 11, (['o', 'y', 'u', 'o'] as const)[frame % 4] as PaletteKey);
    c.set(27, 10, (['u', 'o', 'u', 'y'] as const)[frame % 4] as PaletteKey);
    // The trigger and its guard.
    box(c, 26, 25, 30, 28, [0, 1], (_x, y): PaletteKey => (y === 25 ? 'Y' : 'U'), 'r');
  });

export default sprite({
  name: 'pollinator_pirate',
  ...geometry,
  notes: `Blunderbuss (ADR-0160 amended, the Pirate set's gun): a walnut stock (its grain, a heavy butt dropping behind the
grip, a brass butt plate), a round brass barrel with three bands flaring into a wide bell mouth (rimmed in gold, a dark
hole), a brass lock plate and a matchlock's serpentine arm curling up over it, holding a match whose end smoulders
(orange and gold, flickering over 4 frames). Fire: the bell's mouth flashes white, then gold and orange, settling over 3
frames. Its flair: the match's ember breathing, a wisp of smoke curling up off it, a spark now and then. No team keys.
Rubric (4×): S5 R4 P4 L4 C4 A4 F4.`,
  anims: {
    idle: { fps: 6, frames: [blunderbuss(0), blunderbuss(1), blunderbuss(2), blunderbuss(3)] },
    fire: { fps: 20, loop: false, frames: [blunderbuss(0, 2), blunderbuss(1, 2), blunderbuss(2, 1), blunderbuss(3)] },
  },
});
